import { FC } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';

import { partnersQueries } from '@/api/partners.service';

import { getPublicFileLogo, getPublicFileUrl } from '@/api';
import { PartnerDto } from '@/generated-types';

interface IMetaTagsProps {
	partner: Required<PartnerDto>;
}

const MetaTags: FC<IMetaTagsProps> = ({ partner }) => {
	const publicLogoUrl = getPublicFileLogo(partner?.logo?.id);
	const publicFaviconUrl = getPublicFileUrl(partner?.favicon?.id);

	const { data: finalLogoUrl = '' } = useQuery({
		...partnersQueries.partner.metaLogo(publicLogoUrl),
		enabled: !!publicLogoUrl && !!partner?.logo?.id,
	});

	const { data: finalFaviconUrl = '' } = useQuery({
		...partnersQueries.partner.metaFavicon(publicFaviconUrl),
		enabled: !!publicFaviconUrl && !!partner?.favicon?.id,
	});

	return (
		<Helmet>
			<title>{partner?.name || ''} - SBA Loans</title>
			<meta content={partner?.description || ''} name="description" />

			{/* Open Graph Meta Tags */}
			<meta content="website" property="og:type" />
			<meta content={partner?.name || ''} property="og:title" />
			<meta content={partner?.description || ''} property="og:description" />
			<meta content={partner?.url || ''} property="og:url" />

			<meta content={finalLogoUrl} property="og:image" />
			<meta content={partner?.logo?.originalFilename || ''} property="og:image:alt" />

			<meta content="1200" property="og:image:width" />
			<meta content="630" property="og:image:height" />

			{/* Twitter Card */}
			<meta content="summary_large_image" name="twitter:card" />
			<meta content={partner?.name || ''} name="twitter:title" />
			<meta content={partner?.description || ''} name="twitter:description" />

			<meta content={finalLogoUrl} name="twitter:image" />
			<meta content={partner?.logo?.originalFilename || ''} property="twitter:image:alt" />

			<meta content={partner?.url || ''} property="twitter:url" />

			{/* Favicon */}
			<link href={finalFaviconUrl ?? 'data:'} rel="shortcut icon" />
		</Helmet>
	);
};

export default MetaTags;
